/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { Table } from "@duxton-web/ui"
import { FC } from "react"
import { SellingTime } from "types/models/menu"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import {
  getHasPeriod,
  useTranslatedGroupedServiceHours,
} from "utils/selling_time_helper"
import useSellingTimePeriodRangeText from "hooks/useSellingTimePeriodRangeText"
import { serviceHourStyle } from "./styles"

export interface ServiceHourListProps {
  sellingTime?: SellingTime
}

export const ServiceHourList: FC<ServiceHourListProps> = ({
  sellingTime,
}: ServiceHourListProps) => {
  const periodRangeText = useSellingTimePeriodRangeText(sellingTime)

  const translatedGroupedServiceHours = useTranslatedGroupedServiceHours(
    sellingTime?.groupedServiceHours,
  )

  const textMap = useTranslationsMapTyped({
    periodLabel: "menu.sellingTime.label.period",
  })

  if (!sellingTime) {
    return null
  }

  const hasPeriod = getHasPeriod(sellingTime)

  const getDataSource = () => {
    const result = translatedGroupedServiceHours.map(groupedServiceHour => ({
      key: groupedServiceHour.dayRange,
      dayRange: groupedServiceHour.dayRange,
      periods: groupedServiceHour.periods,
    }))
    if (hasPeriod) {
      result.push({
        key: textMap.periodLabel,
        dayRange: textMap.periodLabel,
        periods: [periodRangeText],
      })
    }
    return result.map(groupedServiceHour => ({
      key: groupedServiceHour.dayRange,
      dayRange: groupedServiceHour.dayRange,
      periods: groupedServiceHour.periods,
    }))
  }

  return (
    <div css={serviceHourStyle}>
      <Table
        dataSource={getDataSource()}
        pagination={false}
        showHeader={false}
        rowKey="key"
      >
        <Table.Column dataIndex="dayRange" className="dayColumn" />
        <Table.Column
          dataIndex="periods"
          className="timingColumn"
          render={periods =>
            periods.map((period: string) => {
              return (
                <div key={period}>
                  {period}
                  <br />
                </div>
              )
            })
          }
        />
      </Table>
    </div>
  )
}
